/** TanStack Query hooks for the review queue and draft change summary. */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { PaginatedResponse, ReviewQueueItem, DraftChangeSummary } from '@formency/shared';

// ─── Query key factory ─────────────────────────────────────────────────────

export const reviewQueueKeys = {
  all: ['f', 'reviewQueue'] as const,
  list: (page: number, sortBy: string, sortOrder: string) =>
    [...reviewQueueKeys.all, 'list', page, sortBy, sortOrder] as const,
  changeSummary: (courseId: string, versionId: string) =>
    ['f', 'courseVersions', courseId, 'changes', versionId] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/** Fetch paginated review queue (in_review versions). */
export function useReviewQueue(options?: {
  page?: number;
  pageSize?: number;
  sortBy?: 'submittedAt' | 'courseTitle';
  sortOrder?: 'asc' | 'desc';
}) {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 20;
  const sortBy = options?.sortBy ?? 'submittedAt';
  const sortOrder = options?.sortOrder ?? 'desc';

  return useQuery<PaginatedResponse<ReviewQueueItem>>({
    queryKey: reviewQueueKeys.list(page, sortBy, sortOrder),
    queryFn: () =>
      api.get<PaginatedResponse<ReviewQueueItem>>('/review-queue', {
        page,
        pageSize,
        sortBy,
        sortOrder,
      }),
  });
}

/** Fetch draft change summary (structural + content diff). */
export function useDraftChangeSummary(courseId: string | undefined, versionId: string | undefined) {
  return useQuery<DraftChangeSummary>({
    queryKey: reviewQueueKeys.changeSummary(courseId!, versionId!),
    queryFn: () =>
      api.get<DraftChangeSummary>(`/courses/${courseId}/versions/${versionId}/changes`),
    enabled: !!courseId && !!versionId,
  });
}
